import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Phone, Mail, MapPin, Clock, ArrowRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import Header from "../components/Header";
import Footer from "../components/Footer";
import api from "../lib/api";
import { usePageSeo } from "../lib/seo";
import { ENTITY_SITE, brokerageNode, agentNode, breadcrumbNode } from "../lib/entityGraph";

const SITE = "https://equitashomes.com";
const CANON = `${ENTITY_SITE}/contact`;
const fade = { initial: { opacity: 0, y: 20 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true, margin: "-80px" }, transition: { duration: 0.6 } };

const METHODS = [
  { icon: Phone, label: "Call or text", value: "(408) 561-1321", href: "tel:+14085611321", tid: "contact-phone" },
  { icon: Mail, label: "Email", value: "louis@equitashomes.com", href: "mailto:louis@equitashomes.com", tid: "contact-email" },
  { icon: MapPin, label: "Serving", value: "San Jose & Santa Clara County", href: null, tid: "contact-area" },
  { icon: Clock, label: "Response time", value: "Same day, 7 days a week", href: null, tid: "contact-hours" },
];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "", company: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  usePageSeo({
    title: "Contact Louis Fong | Equitas Homes 1% Listing Broker, San Jose",
    description: "Contact Louis Fong, Broker at Equitas Homes. Call (408) 561-1321 or message us about selling your San Jose or Santa Clara County home with a 1% listing fee.",
    canonical: CANON,
    jsonLd: {
      "@context": "https://schema.org",
      "@graph": [
        brokerageNode({ contactPoint: { "@type": "ContactPoint", telephone: "+1-408-561-1321", email: "louis@equitashomes.com", contactType: "sales", areaServed: "US-CA", availableLanguage: "English" } }),
        agentNode(),
        breadcrumbNode(`${CANON}#breadcrumb`, [{ name: "Home", item: `${ENTITY_SITE}/` }, { name: "Contact", item: CANON }]),
        { "@type": "ContactPage", "@id": `${CANON}#webpage`, url: CANON, name: "Contact Equitas Homes", mainEntity: { "@id": `${ENTITY_SITE}/#brokerage` } },
      ],
    },
  });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) { toast.error("Please add your name, email, and a message."); return; }
    setSending(true);
    try {
      await api.post("/contact", form);
      setSent(true);
      toast.success("Message sent — Louis will be in touch shortly.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong. Please call (408) 561-1321.");
    } finally { setSending(false); }
  };

  const inp = "mt-1.5 w-full rounded-xl border border-brand-hairline bg-white px-4 py-3 text-sm text-brand-ink outline-none focus:ring-2 focus:ring-brand-emerald/40 focus:border-brand-emerald transition";

  return (
    <div className="min-h-screen bg-brand-cream paper-grain flex flex-col" data-testid="contact-page">
      <Header />
      <main className="flex-1">
        <section className="bg-brand-emerald text-brand-cream">
          <div className="mx-auto max-w-5xl px-5 sm:px-8 py-16 lg:py-20">
            <motion.div {...fade}>
              <span className="eyebrow">Let's Talk</span>
              <h1 className="mt-4 font-serif font-light leading-[1.05] text-4xl sm:text-5xl">Contact Equitas Homes</h1>
              <p className="mt-5 max-w-2xl text-base sm:text-lg leading-relaxed text-brand-cream/75">
                Thinking about selling, or just want a straight answer about your home's value and what you'd keep with a 1% listing fee? Reach Louis Fong directly — no call centers, no pressure.
              </p>
            </motion.div>
          </div>
        </section>

        <section className="mx-auto max-w-7xl px-5 sm:px-8 py-16 lg:py-24">
          <div className="grid gap-12 lg:grid-cols-5 lg:gap-16 items-start">
            {/* Contact methods */}
            <motion.div {...fade} className="lg:col-span-2 space-y-4">
              <h2 className="font-serif text-2xl text-brand-ink">Reach us directly</h2>
              <div className="mt-2 grid gap-3">
                {METHODS.map((m) => {
                  const Inner = (
                    <div className="flex items-start gap-3.5 rounded-2xl bg-white border border-brand-hairline p-5 h-full">
                      <div className="h-10 w-10 rounded-xl bg-brand-emerald/10 flex items-center justify-center shrink-0"><m.icon className="h-5 w-5 text-brand-emerald" /></div>
                      <div>
                        <p className="text-xs uppercase tracking-wider text-brand-stone/70 font-semibold">{m.label}</p>
                        <p className="mt-0.5 text-brand-ink font-medium">{m.value}</p>
                      </div>
                    </div>
                  );
                  return m.href
                    ? <a key={m.tid} href={m.href} data-testid={m.tid} className="block hover:border-brand-emerald transition-colors [&>div]:hover:border-brand-emerald">{Inner}</a>
                    : <div key={m.tid} data-testid={m.tid}>{Inner}</div>;
                })}
              </div>
              <div className="rounded-2xl bg-brand-sand/60 border border-brand-hairline p-5 text-sm text-brand-stone leading-relaxed">
                Prefer to see the numbers first? <Link to="/" className="text-brand-emerald font-medium hover:underline">Get your free home value &amp; net-equity estimate</Link> — then reach out with questions.
              </div>
            </motion.div>

            {/* Form */}
            <motion.div {...fade} className="lg:col-span-3">
              <div className="rounded-2xl bg-white border border-brand-hairline p-6 sm:p-8 shadow-[0_18px_50px_rgba(27,59,43,0.08)]">
                {sent ? (
                  <div className="text-center py-10" data-testid="contact-success">
                    <div className="mx-auto h-14 w-14 rounded-full bg-brand-emerald/10 flex items-center justify-center"><Mail className="h-7 w-7 text-brand-emerald" /></div>
                    <h2 className="mt-5 font-serif text-2xl text-brand-ink">Message sent</h2>
                    <p className="mt-2 text-brand-stone">Thank you — Louis Fong will be in touch shortly. For anything urgent, call <a href="tel:+14085611321" className="text-brand-emerald font-medium hover:underline">(408) 561-1321</a>.</p>
                    <Link to="/" className="mt-6 inline-flex items-center gap-2 rounded-full bg-brand-emerald px-6 py-3 text-sm font-semibold text-brand-cream hover:bg-brand-emeraldHover transition-colors">Get my home value <ArrowRight className="h-4 w-4" /></Link>
                  </div>
                ) : (
                  <form onSubmit={submit} data-testid="contact-form">
                    <h2 className="font-serif text-2xl text-brand-ink">Send a message</h2>
                    <p className="mt-1.5 text-sm text-brand-stone">We'll reply the same day.</p>
                    <input type="text" tabIndex={-1} autoComplete="off" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className="hidden" aria-hidden="true" />
                    <div className="mt-6 grid gap-5 sm:grid-cols-2">
                      <div><label className="text-xs font-semibold text-brand-stone">Name *</label>
                        <input data-testid="contact-input-name" className={inp} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
                      <div><label className="text-xs font-semibold text-brand-stone">Phone</label>
                        <input data-testid="contact-input-phone" className={inp} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
                    </div>
                    <div className="mt-5"><label className="text-xs font-semibold text-brand-stone">Email *</label>
                      <input data-testid="contact-input-email" type="email" className={inp} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
                    <div className="mt-5"><label className="text-xs font-semibold text-brand-stone">How can we help? *</label>
                      <textarea data-testid="contact-input-message" rows={5} className={inp} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Tell us about your home or question…" /></div>
                    <button type="submit" disabled={sending} data-testid="contact-submit"
                      className="mt-6 inline-flex items-center justify-center gap-2 rounded-full bg-brand-emerald px-7 py-3.5 text-sm font-semibold text-brand-cream hover:bg-brand-emeraldHover disabled:opacity-60 transition-colors">
                      {sending ? <><Loader2 className="h-4 w-4 animate-spin" /> Sending…</> : <>Send message <ArrowRight className="h-4 w-4" /></>}
                    </button>
                    <p className="mt-3 text-xs text-brand-stone/60">By submitting, you agree to be contacted about your inquiry. We never share your information.</p>
                  </form>
                )}
              </div>
            </motion.div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
